import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QuickViewModal } from '/Foundation/Common/code/Views/Foundation/Common/QuickViewModal/QuickViewModal.jsx';
import Price from '/Foundation/Common/code/Views/Foundation/Common/Price.jsx';
import './HotSpotImage.less';

const mdMediaQuery = '(min-width: 768px)';
const HOVER_CLOSE_DELAY_MS = 200;

const MobileProductList = ({ hotspots, listTitle, onClose }) => {
  return (
    <QuickViewModal isOpen={true} onRequestClose={onClose} contentLabel={listTitle} shouldCloseOnOverlayClick={true}>
      <div className="hotspotImage__mobileList">
        <h2 className="heading5 mb-0 hotspotImage__mobileListTitle">{listTitle}</h2>
        <ul className="hotspotImage__sheetList">
          {hotspots.map((h, index) => {
            const product = h.Product;
            if (!product) return null;
            const secondaryText = h.SecondaryText || '';
            const rowContent = (
              <>
                {product.ProductImageUrl ? (
                  <span className="hotspotImage__thumbWrap">
                    <img
                      className="hotspotImage__thumb"
                      src={product.ProductImageUrl}
                      alt={product.ProductImageAltText || ''}
                      loading="lazy"
                    />
                  </span>
                ) : null}
                <span className="hotspotImage__sheetText">
                  <span className="hotspotImage__sheetName heading6 text--truncate-2">{product.Name}</span>
                  {secondaryText ? (
                    <div className="hotspotImage__sheetSecondary paragraph--s text-color--gray80 text--truncate-2">
                      {secondaryText}
                    </div>
                  ) : null}
                  <div className="hotspotImage__sheetPrice heading6">
                    <Price price={product.Price} />
                  </div>
                </span>

                <i className="le-icon-arrow rot-180 hotspotImage__sheetArrow" />
              </>
            );

            return (
              <li key={`row-${index}`} className="hotspotImage__sheetRow">
                {product.ProductUrl ? (
                  <a href={product.ProductUrl} className="hotspotImage__sheetRowLink">
                    {rowContent}
                  </a>
                ) : (
                  <div className="hotspotImage__sheetRowLink">{rowContent}</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </QuickViewModal>
  );
};

const HotSpotImage = (props) => {
  const { BackgroundImage, Hotspots, ListTitle } = props;

  const [activeIndex, setActiveIndex] = useState(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const stageRef = useRef(null);
  const closeTimer = useRef(null);

  const hotspots = Hotspots || [];

  useEffect(() => {
    const mq = window.matchMedia(mdMediaQuery);
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => {
    if (!isDesktop || activeIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setActiveIndex(null);
        const dots = stageRef.current?.querySelectorAll('.hotspotImage__dot');
        if (dots?.[activeIndex]) dots[activeIndex].focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDesktop, activeIndex]);

  const handleMarkerEnter = useCallback((index) => {
    clearTimeout(closeTimer.current);
    setActiveIndex(index);
  }, []);

  const handleMarkerLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => setActiveIndex(null), HOVER_CLOSE_DELAY_MS);
  }, []);

  const onHotspotActivate = useCallback(
    (index) => {
      if (isDesktop) {
        setActiveIndex(index);
        return;
      }
      setMobileListOpen(true);
    },
    [isDesktop]
  );

  const imgSrc = BackgroundImage?.Src || '';
  const imgAlt = BackgroundImage?.Alt || '';

  return (
    <div className="hotspotImage">
      <div className="hotspotImage__stage" ref={stageRef}>
        {imgSrc ? <img className="hotspotImage__img" src={imgSrc} alt={imgAlt} loading="lazy" /> : null}
        {hotspots.map((h, index) => {
          const product = h.Product;
          if (!product) return null;
          const left = h.Left != null ? Number(h.Left) : 0;
          const top = h.Top != null ? Number(h.Top) : 0;
          const stageWidth = stageRef.current?.offsetWidth || 0;
          const markerRight = (left / 100) * stageWidth + 23;
          const cardFlipped = markerRight + 12 + 216 > stageWidth;
          const isActive = activeIndex === index;
          const secondaryText = h.SecondaryText || '';

          return (
            <div
              key={`${h.ProductId || index}-${index}`}
              className={`hotspotImage__marker${isActive ? ' hotspotImage__marker--active' : ''}`}
              style={{ left: `${left}%`, top: `${top}%` }}
              onMouseEnter={() => isDesktop && handleMarkerEnter(index)}
              onMouseLeave={() => isDesktop && handleMarkerLeave()}
            >
              <button
                type="button"
                className="hotspotImage__dot"
                aria-expanded={isDesktop ? isActive : mobileListOpen}
                aria-haspopup={isDesktop ? 'true' : 'dialog'}
                aria-label={product?.Name || 'Hot spot'}
                onClick={() => onHotspotActivate(index)}
                onFocus={() => isDesktop && setActiveIndex(index)}
                onBlur={(e) => {
                  if (isDesktop && !e.currentTarget.parentElement.contains(e.relatedTarget)) {
                    setActiveIndex(null);
                  }
                }}
              />
              {isDesktop && isActive && product ? (
                <a
                  href={product.ProductUrl || '#'}
                  className={`hotspotImage__card${cardFlipped ? ' hotspotImage__card--left' : ''}`}
                  onMouseEnter={() => clearTimeout(closeTimer.current)}
                  onBlur={(e) => {
                    if (!e.currentTarget.closest('.hotspotImage__marker').contains(e.relatedTarget)) {
                      setActiveIndex(null);
                    }
                  }}
                >
                  <div className="hotspotImage__cardBody">
                    <div className="hotspotImage__cardTitle heading5 text--truncate-2">{product.Name}</div>
                    {secondaryText ? (
                      <div className="hotspotImage__cardSecondary paragraph--s text-color--gray80 text--truncate-2">
                        {secondaryText}
                      </div>
                    ) : null}
                    <div className="hotspotImage__cardPrice heading6">
                      <Price price={product.Price} />
                    </div>
                    <div className="hotspotImage__cardArrow">
                      <i className="le-icon-arrow rot-180" />
                    </div>
                  </div>
                </a>
              ) : null}
            </div>
          );
        })}
      </div>

      {mobileListOpen ? (
        <MobileProductList hotspots={hotspots} listTitle={ListTitle} onClose={() => setMobileListOpen(false)} />
      ) : null}
    </div>
  );
};

const byId = typeof window !== 'undefined' && window._THULEDATA && window._THULEDATA._hotspotImageById;

if (byId && typeof byId === 'object') {
  Object.keys(byId).forEach((instanceId) => {
    const rootEl = document.getElementById(`REACT_hotspotImage_${instanceId}`);
    const payload = byId[instanceId];
    if (!rootEl || !payload) return;
    const root = createRoot(rootEl);
    root.render(
      <Sentry.ErrorBoundary>
        <HotSpotImage {...payload} />
      </Sentry.ErrorBoundary>
    );
  });
}

export default HotSpotImage;
